import { useBuyerStore, useBuyerStoreData } from "@/providers/global";
import { useEffect } from "react";

/**
 * Custom hook for initializing favorites from localStorage
 * This hook should be used once in the app root or main layout
 */
export function useFavoritesInitialization() {
	const buyerStore = useBuyerStore();

	useEffect(() => {
		// Initialize favorites from localStorage on mount
		// Only run once on component mount
		if (typeof window !== "undefined") {
			try {
				const storedFavorites = localStorage.getItem("tray_properties");
				if (storedFavorites) {
					const favorites = JSON.parse(storedFavorites);
					// Access store function directly to avoid dependency
					buyerStore.getState().initFavorites(favorites);
				}
			} catch (_error) {
				// console.error("Error loading favorites from localStorage:", _error);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array - only run once on mount
}

/**
 * Custom hook for managing favorites
 * This hook provides access to favorites state and actions from the global buyer store
 */
export function useFavorites() {
	const favorites = useBuyerStoreData(state => state.favorites);
	const setFavorites = useBuyerStoreData(state => state.setFavorites);
	const toggleFavorite = useBuyerStoreData(state => state.toggleFavorite);
	const clearFavorites = useBuyerStoreData(state => state.clearFavorites);

	return {
		favorites,
		setFavorites,
		toggleFavorite,
		clearFavorites,
	};
}

/**
 * Custom hook for checking if a specific item is favorited
 * This hook only re-renders when the favorite status of the specific item changes
 */
export function useIsFavorite(itemId: string) {
	const favorites = useBuyerStoreData(state => state.favorites);
	return favorites.some(fav => fav._id === itemId);
}

/**
 * Custom hook for toggling a specific item's favorite status
 * This hook provides an optimized toggle function for a specific item
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useToggleFavorite(item: any) {
	const toggleFavorite = useBuyerStoreData(state => state.toggleFavorite);
	return () => toggleFavorite(item);
}
